import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";

import {
  type BillingAction,
  type BillingTier,
  formatMicroUsd,
  percentUsed,
} from "../../../../../shared/billing";
import { AiCreditsInfo } from "../../../../billing/AiCreditsInfo";
import {
  BILLING_KEY,
  type BillingSummary,
  type CheckoutKind,
  type PaymentInfo,
  openPortal,
  startCheckout,
} from "../../../../billing/api";
import { useBilling } from "../../../../billing/use-billing";

export const Route = createFileRoute("/_workspace/settings/billing/")({
  component: BillingPane,
});

const TIER_LABEL: Record<BillingTier, string> = {
  free: "Getting started",
  trial: "Trial",
  subscribed: "Hearth Standard",
  lapsed: "Subscription ended",
};

/** What the gate wants next, as pane-level urgency copy. */
const ACTION_COPY: Record<BillingAction, string> = {
  try5: "Your assistant is paused. Buy AI credits to keep building.",
  subscribe:
    "Your assistant needs an active subscription. Subscribe to keep it running.",
  topup: "Your monthly credit is used up. Top up to keep going until renewal.",
};

function BillingPane() {
  const billing = useBilling();
  const qc = useQueryClient();

  // Back from Stripe Checkout: thank or shrug, then strip the marker so a
  // reload doesn't repeat it. The credit itself arrives via webhook — the
  // query refetch races it, so keep polling until the balance reflects it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("checkout");
    if (!result) return;
    if (result === "success") {
      toast.success("Payment received. Your credits are on the way.");
      void qc.invalidateQueries({ queryKey: BILLING_KEY });
    } else if (result === "canceled") {
      toast("Checkout canceled.");
    }
    params.delete("checkout");
    const rest = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${rest ? `?${rest}` : ""}`,
    );
  }, [qc]);

  const checkout = useMutation({
    mutationFn: (kind: CheckoutKind) => startCheckout(kind),
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err) => toast.error(err.message),
  });
  const portal = useMutation({
    mutationFn: openPortal,
    onError: (err) => toast.error(err.message),
  });
  // The portal opens in a NEW window (you go manage, you come back). The
  // window must be opened synchronously in the click — a popup opened after
  // the await gets blocked — then pointed at the URL when Stripe answers.
  const openPortalTab = () => {
    const win = window.open("about:blank", "_blank");
    portal.mutate(undefined, {
      onSuccess: (url) => {
        if (win) win.location.href = url;
        else window.location.href = url;
      },
      onError: () => win?.close(),
    });
  };

  // Same page skeleton as every settings pane: centered column, h1, sections.
  const frame = (children: ReactNode) => (
    <div className="mx-auto w-full max-w-2xl px-6 pt-8 pb-12">
      <h1 className="text-lg font-semibold">Billing</h1>
      <div className="mt-6 space-y-8">{children}</div>
    </div>
  );

  if (billing.isPending) {
    return frame(
      <>
        <Skeleton className="h-44 w-full" />
        <Skeleton className="h-32 w-full" />
      </>,
    );
  }
  if (billing.isError || !billing.data) {
    return frame(
      <p className="text-sm text-muted-foreground">
        Couldn't load billing{billing.error ? `: ${billing.error.message}` : ""}
        .
      </p>,
    );
  }
  const summary = billing.data;
  const { status } = summary;
  const busy = checkout.isPending || portal.isPending;

  return frame(
    <>
      {status.action ? (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm"
        >
          <p className="text-destructive">{ACTION_COPY[status.action]}</p>
          <div className="flex shrink-0 items-center gap-2">
            {status.action !== "subscribe" && summary.checkout.pack ? (
              <Button
                variant="destructive"
                size="sm"
                disabled={busy}
                onClick={() => checkout.mutate("pack")}
              >
                {status.action === "topup" ? "Buy credits" : "Get credits"}
              </Button>
            ) : null}
            {status.action !== "topup" && summary.checkout.monthly ? (
              <Button
                variant={
                  status.action === "subscribe" ? "destructive" : "outline"
                }
                size="sm"
                disabled={busy}
                onClick={() => checkout.mutate("subscribe-monthly")}
              >
                Subscribe
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <Section title="Current plan">
        <CurrentPlanCard
          summary={summary}
          busy={busy}
          onBuy={checkout.mutate}
          onPortal={openPortalTab}
        />
      </Section>

      {status.tier !== "subscribed" &&
      (summary.checkout.monthly || summary.checkout.annual) ? (
        <Section title="Plans">
          <StandardPlanCard
            summary={summary}
            busy={busy}
            onBuy={checkout.mutate}
          />
        </Section>
      ) : null}

      {status.tier === "subscribed" && summary.checkout.pack ? (
        <Section title="Extras">
          <TopUpCard summary={summary} busy={busy} onBuy={checkout.mutate} />
        </Section>
      ) : null}

      {summary.payments.length > 0 ? (
        <Section
          title="Payment history"
          action={
            summary.manageable ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={openPortalTab}
                className="text-muted-foreground hover:text-foreground"
              >
                View all in Stripe
                <ExternalLink />
              </Button>
            ) : undefined
          }
        >
          <PaymentsList payments={summary.payments} />
        </Section>
      ) : null}

      <p className="text-xs text-muted-foreground">
        Unused purchased credits are refundable on request,{" "}
        <a
          href="mailto:billing@ourhearth.ai?subject=Refund%20request"
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-2 hover:text-foreground"
        >
          just ask us
        </a>
        .
      </p>
    </>,
  );
}

/** Section: small uppercase label, optional right action. No hint prose. */
function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * What you currently have. The free tier's card IS the "Try for $5" prompt
 * — a member with no purchases has no plan facts worth listing, only the
 * next step. Balance displays are percentages, never dollars; dollar figures
 * on this page are prices only.
 */
function CurrentPlanCard({
  summary,
  busy,
  onBuy,
  onPortal,
}: {
  summary: BillingSummary;
  busy: boolean;
  onBuy: (kind: CheckoutKind) => void;
  onPortal: () => void;
}) {
  const { status, prices, checkout } = summary;
  const sub = status.subscription;
  const packAmount = prices.pack
    ? formatMicroUsd(prices.pack.amountMicro)
    : null;

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <span className="text-base font-semibold">
          {TIER_LABEL[status.tier]}
        </span>
        <PlanBadge summary={summary} />
      </div>
      {summary.manageable && status.tier === "subscribed" ? (
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={onPortal}
          className="text-muted-foreground hover:text-foreground"
        >
          Manage subscription
          <ExternalLink />
        </Button>
      ) : null}
    </div>
  );

  if (status.tier === "free") {
    return (
      <Card>
        <CardContent className="space-y-3 pt-6">
          {header}
          <p className="text-sm text-foreground/90">
            Buy {packAmount ?? "a one-time pack"} in AI credits{" "}
            <AiCreditsInfo /> to build out your assistant. No time limit, and
            whatever's left is yours to keep when you subscribe.
          </p>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            {checkout.pack ? (
              <Button size="sm" disabled={busy} onClick={() => onBuy("pack")}>
                Get credits
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                Purchases aren't available on this deployment.
              </span>
            )}
            <p className="text-xs text-muted-foreground">
              Notes, apps, and your data are always free.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status.tier === "trial") {
    const pct = percentUsed(status.balanceMicro, status.purchasedGrantedMicro);
    return (
      <Card>
        <CardContent className="space-y-3 pt-6">
          {header}
          <CreditBar
            label="AI Credits"
            percent={pct}
            caption={
              <>
                <span className="font-medium text-foreground">
                  {formatMicroUsd(status.balanceMicro)}
                </span>{" "}
                available
              </>
            }
          />
          <p className="text-sm text-muted-foreground">
            Credits never expire. Subscribe any time and whatever's left stays
            in your wallet.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (status.tier === "subscribed") {
    const monthlyPrice = prices.monthly;
    const monthlyPct = percentUsed(
      status.monthlyRemainingMicro,
      status.monthlyGrantMicro,
    );
    // The monthly credit resets on its own MONTH anniversary — on annual
    // plans that's a different (much nearer) date than the renewal.
    const resetDate = status.monthlyResetMs
      ? formatShortDate(status.monthlyResetMs)
      : sub?.periodEndMs
        ? formatShortDate(sub.periodEndMs)
        : null;
    const renewal = sub?.periodEndMs
      ? `${sub.cancelAtPeriodEnd ? "Ends" : "Renews"} ${formatShortDate(sub.periodEndMs)}`
      : null;
    // Show the price the member actually pays. The interval is the paid
    // period the invoice stamped on the mirror — NOT anything derivable from
    // the grant, which is one month's credit on annual plans too.
    const annual = (sub?.creditMonths ?? 1) >= 12;
    const priceLine = annual
      ? prices.annual
        ? `${formatMicroUsd(prices.annual.amountMicro)} / year`
        : null
      : monthlyPrice
        ? `${formatMicroUsd(monthlyPrice.amountMicro)} / month`
        : null;
    return (
      <Card>
        <CardContent className="space-y-4 pt-6">
          {header}
          {priceLine ? (
            <p className="text-sm text-muted-foreground">
              {priceLine}
              {renewal ? ` · ${renewal}` : ""}
            </p>
          ) : null}
          <CreditBar
            label="Included monthly credit"
            percent={monthlyPct}
            caption={resetDate ? `Resets ${resetDate}` : "Resets at renewal"}
          />
          {/* The wallet gets a BALANCE, not a meter — it's prepaid money, and
              "how much is banked" is the only question it answers. */}
          {status.purchasedRemainingMicro > 0 ? (
            <div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-medium">Credits remaining</span>
                <span className="text-lg font-semibold tabular-nums">
                  {formatMicroUsd(status.purchasedRemainingMicro)}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Never expire · spent after the monthly credit runs out
              </p>
            </div>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  // Lapsed: the subscription ended, so the assistant is paused — after the
  // trial, spending credits requires an active subscription. Whatever's
  // banked is kept (and refundable), shown as a wallet balance, not a meter.
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        {header}
        {status.balanceMicro > 0 ? (
          <div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">Credits remaining</span>
              <span className="text-lg font-semibold tabular-nums">
                {formatMicroUsd(status.balanceMicro)}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Never expire · usable again once you subscribe
            </p>
          </div>
        ) : null}
        <p className="text-sm text-muted-foreground">
          Your assistant is paused until you subscribe again. Notes, apps, and
          your data stay free.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * The percentage meter — counts UP from 0% used. Allowance meters show no
 * dollar amounts; the WALLET's caption does (it's prepaid, refundable money
 * — "how much do I have banked" is a dollar question).
 */
function CreditBar({
  label,
  percent,
  caption,
}: {
  label: string;
  percent: number;
  caption?: ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {percent}% used
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
        className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-border"
      >
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${percent}%` }}
        />
      </div>
      {caption ? (
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
      ) : null}
    </div>
  );
}

function PlanBadge({ summary }: { summary: BillingSummary }) {
  const { status } = summary;
  const sub = status.subscription;
  if (status.tier === "subscribed") {
    return sub?.status === "past_due" ? (
      <Badge variant="destructive">Past due</Badge>
    ) : sub?.cancelAtPeriodEnd ? (
      <Badge variant="outline">Ending</Badge>
    ) : (
      <Badge variant="secondary">Active</Badge>
    );
  }
  // Lapsed is gated regardless of balance — "out of credits" would be a lie
  // when the wallet still holds some.
  if (status.tier === "lapsed")
    return <Badge variant="destructive">Paused</Badge>;
  if (status.gated) return <Badge variant="destructive">Out of credits</Badge>;
  return null;
}

/**
 * The one plan. Credit packs are deliberately NOT here — packs are credit
 * inventory, not a plan (Justin, 2026-08-04); they're bought from the
 * current-plan card where they apply.
 */
function StandardPlanCard({
  summary,
  busy,
  onBuy,
}: {
  summary: BillingSummary;
  busy: boolean;
  onBuy: (kind: CheckoutKind) => void;
}) {
  const { checkout, prices } = summary;
  const credit = prices.monthly?.creditMonthlyMicro;
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-base font-semibold">Hearth Standard</div>
            {prices.monthly ? (
              <p className="text-sm text-muted-foreground">
                {formatMicroUsd(prices.monthly.amountMicro)} / month
                {prices.annual
                  ? ` · ${formatMicroUsd(prices.annual.amountMicro)} / year`
                  : ""}
              </p>
            ) : null}
          </div>
          {/* ONE button: the session is minted on the monthly price and the
              monthly→annual choice happens INSIDE Stripe Checkout (the upsell
              configured on the monthly price shows the annual toggle with the
              savings). Annual-direct remains only as the fallback when monthly
              isn't configured. */}
          {checkout.monthly ? (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => onBuy("subscribe-monthly")}
            >
              Subscribe
            </Button>
          ) : checkout.annual ? (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => onBuy("subscribe-annual")}
            >
              Subscribe annually
            </Button>
          ) : null}
        </div>
        <div className="border-t border-border" />
        <ul className="space-y-1.5 text-sm text-foreground/90">
          <li>
            • <strong className="font-semibold">A home</strong> for your
            family's notes and documents, online or off
          </li>
          <li>
            • <strong className="font-semibold">Sync</strong> across every
            device and family member
          </li>
          <li>
            • <strong className="font-semibold">An AI assistant</strong> that
            writes custom apps for your life
          </li>
          <li>
            • <strong className="font-semibold">Scheduled</strong> prompts and
            actions
          </li>
          <li>
            • <strong className="font-semibold">Works with</strong> Google
            Calendar, Gmail, and the other services you use
          </li>
          <li>
            •{" "}
            <strong className="font-semibold">
              {credit != null
                ? `${formatMicroUsd(credit)} of AI credit`
                : "AI credit"}
            </strong>{" "}
            included every month
          </li>
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * The subscriber's top-up "plan": what a top-up IS, with the buy button.
 * Broken out of the current-plan card so credits-you-buy reads as its own
 * thing, orthogonal to the subscription.
 */
function TopUpCard({
  summary,
  busy,
  onBuy,
}: {
  summary: BillingSummary;
  busy: boolean;
  onBuy: (kind: CheckoutKind) => void;
}) {
  const pack = summary.prices.pack;
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div>
          <p className="text-base font-semibold">Top up AI credits</p>
          {pack ? (
            <p className="mt-0.5 text-sm text-muted-foreground">
              {formatMicroUsd(pack.amountMicro)} · one-time
            </p>
          ) : null}
        </div>
        <p className="text-sm text-foreground/90">
          More AI credits for a heavy month. They're spent only after your
          included monthly credit runs out, they never expire, and they stay
          yours even if you cancel.
        </p>
        <Button size="sm" disabled={busy} onClick={() => onBuy("pack")}>
          Buy credits
        </Button>
      </CardContent>
    </Card>
  );
}

/** The Stripe invoice history, as a flat table — Stripe hosts the detail pages. */
function PaymentsList({ payments }: { payments: PaymentInfo[] }) {
  return (
    <Table>
      <TableBody>
        {payments.map((p) => (
          <TableRow key={p.id}>
            <TableCell className="pl-0">
              <p className="text-sm font-medium">
                {p.kind === "subscription" ? "Hearth Standard" : "AI credits"}
              </p>
              <p className="text-xs text-muted-foreground">
                {p.periodStartMs !== null || p.periodEndMs !== null
                  ? formatPeriod(p.periodStartMs, p.periodEndMs)
                  : new Date(p.createdMs).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
              </p>
            </TableCell>
            <TableCell className="text-right text-sm font-medium tabular-nums">
              {formatMicroUsd(p.amountMicro)}
            </TableCell>
            <TableCell className="w-44 pr-0 text-right">
              <span className="inline-flex items-center gap-3">
                {p.refundedMicro > 0 ? (
                  <Badge variant="outline">
                    {p.refundedMicro >= p.amountMicro
                      ? "Refunded"
                      : "Partially refunded"}
                  </Badge>
                ) : null}
                {/* Invoice first: Receipt exists on EVERY row, so keeping it
                    last makes the receipts align in one vertical column. */}
                {p.invoiceUrl ? (
                  <PaymentLink href={p.invoiceUrl} label="Invoice" />
                ) : null}
                {p.receiptUrl ? (
                  <PaymentLink href={p.receiptUrl} label="Receipt" />
                ) : null}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function PaymentLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
    >
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

/**
 * Month + day, with the year whenever it isn't THIS year — "Resets Aug 4"
 * meaning next August reads as today without it.
 */
function formatShortDate(ms: number): string {
  const d = new Date(ms);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

function formatPeriod(startMs: number | null, endMs: number | null): string {
  if (startMs === null && endMs === null) return "";
  const fmt = (ms: number) =>
    new Date(ms).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  if (startMs !== null && endMs !== null)
    return `${fmt(startMs)} – ${fmt(endMs)}`;
  return fmt((startMs ?? endMs) as number);
}
